import { computed, inject } from '@angular/core';
import { patchState, signalStore, type, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { addEntities, entityConfig, removeAllEntities, setAllEntities, withEntities } from '@ngrx/signals/entities';
import EstensaModel from 'estensa-module';
import { Typology } from '../../broker.models';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, pipe, switchMap, tap } from 'rxjs';
import { RadiologyCommonService } from '../../../services/angularjs/radiology-common.service';
import { TranslateService } from '@ngx-translate/core';
import { BrokerService } from '../../../services/angularjs/broker.service';
import { convertStudiesToPacsData } from '../../broker.helper';
import { fromNg1PacsStudyData } from '../../../services/imaging/common';
import { ImagingService } from '../../../services/imaging/imaging.service';
import { ModalDialogNotificationService } from '../../../services/notification.service';

const studyConfig = entityConfig({
    entity: type<EstensaModel.BrokerDataStudy>(),
    collection: 'study',
    selectId: (study) => study.studyUID,
});

const examConfig = entityConfig({
    entity: type<EstensaModel.BrokerDataExam>(),
    collection: 'exam',
    selectId: (exam) => exam.idExam,
});

const sexesConfig = entityConfig({
    entity: type<EstensaModel.Sex>(),
    collection: 'sexes',
    selectId: (sex) => sex.id,
});

const modalitiesConfig = entityConfig({
    entity: type<EstensaModel.CodeDescriptionItem>(),
    collection: 'modalities',
    selectId: (modality) => modality.code,
});

const typorlogyConfig = entityConfig({
    entity: type<Typology>(),
    collection: 'typologies',
    selectId: (typology) => typology.code,
});

export type BrokerAssocState = {
    brokerParams: EstensaModel.BrokerManagementParams | null;
    studyFilterSearch: EstensaModel.BrokerDataFilterStudy | null;
    selectedStudies: EstensaModel.BrokerDataStudy[] | null;
    selectedExams: EstensaModel.BrokerDataExam[] | null;
};

const initialState: BrokerAssocState = {
    brokerParams: null,
    studyFilterSearch: null,
    selectedStudies: null,
    selectedExams: null,
};

export const BrokerAssocSignalStore = signalStore(
    { providedIn: 'root' },
    withEntities(studyConfig),
    withEntities(examConfig),
    withEntities(sexesConfig),
    withEntities(modalitiesConfig),
    withEntities(typorlogyConfig),
    withState<BrokerAssocState>(initialState),
    withComputed((_entities) => ({
        // 👇 exposing entity arrays publicly
        exams: _entities.examEntities,
        studies: _entities.studyEntities,
        sexes: _entities.sexesEntities,
        modalities: _entities.modalitiesEntities,
        typologies: _entities.typologiesEntities,
    })),

    withComputed(({ exams, brokerParams }) => ({
        /*    studyFilterSearch: computed(() => {
            let searchStudyData = brokerParams()?.searchStudyData;
            const _exams = exams();
            if (
                searchStudyData &&
                searchStudyData.typeAssociation === EstensaModel.BrokerTypeFilter.TypeAscToPre &&
                _exams.length > 0
            ) {
                searchStudyData.idExamsToPre = '';
                _exams.forEach((val) => {
                    searchStudyData.idExamsToPre += val.idExam + ',';
                });
            }
            return searchStudyData;
        }), */
    })),

    //ENTINTY METHODS
    withMethods((store) => ({
        addStudies(studies: EstensaModel.BrokerDataStudy[]): void {
            patchState(store, setAllEntities(studies, studyConfig));
        },
        addExams(exams: EstensaModel.BrokerDataExam[]): void {
            patchState(store, setAllEntities(exams, examConfig));
        },
        addSexes(sexes: EstensaModel.Sex[]): void {
            patchState(store, setAllEntities(sexes, sexesConfig));
        },
        /**
         * Set the current management parameters.
         * @param brokerCheckParams Broker check parameters
         * @param bFindStudyAssoc Whether to find study association
         * @param exams Exams to associate
         */
        setCurrentManagementParams(
            brokerCheckParams: EstensaModel.BrokerCheckParams,
            bFindStudyAssoc: boolean,
            exams: EstensaModel.BrokerDataExam[],
        ): void {
            // Create a new instance of BrokerManagementParams to store the updated parameters
            let brokerManagementModalParams = new EstensaModel.BrokerManagementParams();

            // Set the parameters for checking the broker using the provided broker check parameters,
            // whether to find study association, and exams
            brokerManagementModalParams.setParamsForCheckBroker(brokerCheckParams, bFindStudyAssoc, exams);

            // Get the search study data from the updated broker management parameters
            let searchStudyData = brokerManagementModalParams?.searchStudyData;

            // Create a copy of the exams array to avoid modifying the original array
            const _exams = exams;

            // Check if search study data exists, the association type is "AscToPre", and there are exams
            if (
                searchStudyData &&
                searchStudyData.typeAssociation === EstensaModel.BrokerTypeFilter.TypeAscToPre &&
                _exams.length > 0
            ) {
                // Reset the idExamsToPre property to an empty string
                searchStudyData.idExamsToPre = '';

                // Iterate through the exams and append their IDs to the idExamsToPre property
                _exams.forEach((val) => {
                    searchStudyData.idExamsToPre += val.idExam + ',';
                });
            }

            // Update the store with the updated broker parameters and study filter search data
            return patchState(store, {
                brokerParams: brokerManagementModalParams,
                studyFilterSearch: searchStudyData,
            });
        },

        setManualStudiesSearchParams(studyFilterSearch: EstensaModel.BrokerDataFilterStudy): void {
            return patchState(store, { studyFilterSearch });
        },

        addModalities(modalities: EstensaModel.CodeDescriptionItem[]): void {
            patchState(store, setAllEntities(modalities, modalitiesConfig));
        },

        addTypologies(typologies: Typology[]): void {
            patchState(store, setAllEntities(typologies, typorlogyConfig));
        },

        resetStore() {
            patchState(store, removeAllEntities({ collection: 'study' }));
            patchState(store, removeAllEntities({ collection: 'exam' }));
            patchState(store, removeAllEntities({ collection: 'sexes' }));
            patchState(store, removeAllEntities({ collection: 'modalities' }));
            patchState(store, removeAllEntities({ collection: 'typologies' }));
            patchState(store, {
                brokerParams: null,
                selectedStudies: null,
                studyFilterSearch: null,
            });
        },
        setCurrentStudies(selectedStudies: EstensaModel.BrokerDataStudy[]): void {
            patchState(store, {
                selectedStudies,
            });
        },

        setCurrentExams(selectedExams: EstensaModel.BrokerDataExam[]): void {
            patchState(store, {
                selectedExams,
            });
        },
    })),
    withMethods((store, brokerService = inject(BrokerService)) => ({
        loadExams: rxMethod<EstensaModel.BrokerManagementParams | null>(
            pipe(
                switchMap((searchParams: EstensaModel.BrokerManagementParams | null) => {
                    return brokerService.findExams(searchParams?.searchExamData).pipe(
                        tap((response) => {
                            if (response.success) {
                                if (response.data && response.data.length > 0) {
                                    return store.addExams(response.data);
                                } else if (response.data && response.data.length == 0) {
                                    return patchState(store, removeAllEntities({ collection: 'exam' }));
                                }
                            }
                        }),
                    );
                }),
            ),
        ),

        loadStudies: rxMethod<EstensaModel.BrokerDataFilterStudy>(
            pipe(
                switchMap((searchStudyData: EstensaModel.BrokerDataFilterStudy) => {
                    // studi associati alle prestazioni in params
                    return brokerService._findStudies(searchStudyData).pipe(
                        tap((response) => {
                            if (response.success && response.data && response.data.length > 0) {
                                return store.addStudies(response.data);
                            } else if (response.data && response.data.length == 0) {
                                return patchState(store, removeAllEntities({ collection: 'study' }));
                            }
                        }),
                    );
                }),
            ),
        ),
    })),

    withMethods(
        (
            store,
            imagingService = inject(ImagingService),
            brokerService = inject(BrokerService),
            modalDialogNotificationService = inject(ModalDialogNotificationService),
            translate = inject(TranslateService),
        ) => ({
            associateStudies: rxMethod<void>(
                pipe(
                    switchMap(() => {
                        // Create an empty array to store association data 📝
                        let list = new Array<EstensaModel.BrokerSetAssociationData>();

                        // Loop through selected exams and studies 🔄
                        store.selectedExams().forEach((exam: EstensaModel.BrokerDataExam) => {
                            store.selectedStudies().forEach((study: EstensaModel.BrokerDataStudy) => {
                                // Create a new association data item 📈
                                let item = new EstensaModel.BrokerSetAssociationData();
                                item.dataExam = exam;
                                item.dataStudy = study;
                                // Add the item to the list 📦
                                list.push(item);
                            });
                        });

                        // Call the broker service to associate studies 📞
                        return brokerService.associateStudies(list).pipe(
                            tap((response) => {
                                if (response.success && response.data) {
                                    // Initialize variables to track failed associations and error messages 📝
                                    var messageDataInfo = null;
                                    let iFailed = 0;

                                    // Loop through the response data to check for failed associations 🔄
                                    response.data.forEach((item: EstensaModel.BrokerSetAssociationData) => {
                                        if (item.codeMessageNumber != 0) {
                                            // Increment the failed count and append the error message 📝
                                            iFailed++;
                                            messageDataInfo += ' - ' + item.codeMessageString;
                                        }
                                    });

                                    // Display a success or warning message based on the outcome 📣
                                    if (messageDataInfo == null) {
                                        modalDialogNotificationService.success(
                                            translate.instant('AssociateSuccessful'),
                                        );
                                    } else {
                                        modalDialogNotificationService.warning(
                                            translate.instant('Associate'),
                                            messageDataInfo,
                                        );
                                    }

                                    const params = store.studyFilterSearch();
                                    store.loadStudies(params);
                                    return EMPTY;
                                } else {
                                    // Display a failure message if the response was not successful 📣
                                    modalDialogNotificationService.warning(translate.instant('AssociateFailed'));
                                    return EMPTY;
                                }
                            }),
                        );
                    }),
                ),
            ),

            removeAssociation: rxMethod<void>(
                pipe(
                    switchMap(() => {
                        // Create an empty array to store association data 📝
                        let list = new Array<EstensaModel.BrokerSetAssociationData>();

                        // Loop through selected exams and studies 🔄
                        store.selectedExams().forEach((exam: EstensaModel.BrokerDataExam) => {
                            store.selectedStudies().forEach((study: EstensaModel.BrokerDataStudy) => {
                                // Create a new association data item 📈
                                let item = new EstensaModel.BrokerSetAssociationData();
                                item.dataExam = exam;
                                item.dataStudy = study;

                                // Add the item to the list 📦
                                list.push(item);
                            });
                        });

                        // Call the broker service to remove association  📞
                        return brokerService.removeAssociation(list).pipe(
                            tap((response) => {
                                if (response.success && response.data) {
                                    var messageDataInfo = null;
                                    let iFailed = 0;
                                    response.data.forEach((item: EstensaModel.BrokerSetAssociationData) => {
                                        if (item.codeMessageNumber != 0) {
                                            // Increment the failed count and append the error message 📝
                                            iFailed++;
                                            messageDataInfo += ' - ' + item.codeMessageString;
                                        }
                                    });

                                    // Display a success or warning message based on the outcome 📣
                                    if (messageDataInfo == null) {
                                        modalDialogNotificationService.success(
                                            translate.instant('RemoveAssociationSuccessful'),
                                        );
                                    } else {
                                        modalDialogNotificationService.warning(
                                            translate.instant('RemoveAssociation'),
                                            messageDataInfo,
                                        );
                                    }

                                    const params = store.studyFilterSearch();
                                    store.loadStudies(params);
                                    return EMPTY;
                                } else {
                                    // Display a failure message if the response was not successful 📣
                                    modalDialogNotificationService.warning(translate.instant('RemoveAssociation'));
                                    return EMPTY;
                                }
                            }),
                        );
                    }),
                ),
            ),

            validateAssociation: rxMethod<void>(
                pipe(
                    switchMap(() => {
                        // Create an empty array to store association data 📝
                        let list = new Array<EstensaModel.BrokerSetAssociationData>();
                        store.selectedExams().forEach((exam: EstensaModel.BrokerDataExam) => {
                            store.selectedStudies().forEach((study: EstensaModel.BrokerDataStudy) => {
                                let item = new EstensaModel.BrokerSetAssociationData();
                                item.dataExam = exam;
                                item.dataStudy = study;

                                list.push(item);
                            });
                        });

                        // Call the broker service to validate association 📞
                        return brokerService.validateAssociation(list).pipe(
                            tap((response) => {
                                if (response.success && response.data) {
                                    // Initialize variables to track failed associations and error messages 📝
                                    var messageDataInfo = null;
                                    let iFailed = 0;

                                    // Loop through the response data to check for failed associations 🔄
                                    response.data.forEach((item: EstensaModel.BrokerSetAssociationData) => {
                                        if (item.codeMessageNumber != 0) {
                                            // Increment the failed count and append the error message 📝
                                            iFailed++;
                                            messageDataInfo += ' - ' + item.codeMessageString;
                                        }
                                    });

                                    // Display a success message, even if there are errors 📣
                                    if (messageDataInfo == null) {
                                        modalDialogNotificationService.success(
                                            translate.instant('ValidateAssociationSuccessful'),
                                        );
                                    } else {
                                        modalDialogNotificationService.success(
                                            translate.instant('ValidateAssociation'),
                                            messageDataInfo,
                                        );
                                    }

                                    const params = store.studyFilterSearch();
                                    store.loadStudies(params);
                                    return EMPTY;
                                } else {
                                    // Display a failure message if the response was not successful 📣
                                    modalDialogNotificationService.warning(
                                        translate.instant('ValidateAssociationFailed'),
                                    );
                                }
                            }),
                        );
                    }),
                ),
            ),

            onOpenViewer: rxMethod<void>(
                pipe(
                    switchMap(() => {
                        // Get the search parameters from the store 📝
                        const searchParams: EstensaModel.BrokerCheckParams | undefined =
                            store.brokerParams()?.examsCheckParams;

                        // Convert the selected studies to PACS data 🔄
                        const pacsStudyDataList: EstensaModel.PacsStudyData[] = convertStudiesToPacsData(
                            store.selectedStudies(),
                        );

                        // Map the PACS data to a new format 🗺️
                        const list = pacsStudyDataList.map((d) => {
                            return fromNg1PacsStudyData(d);
                        });

                        // Call the imaging service to view images 📸
                        return imagingService.viewImages(list, searchParams?.credentials, searchParams?.openParams);
                    }),
                ),
            ),
        }),
    ),

    withMethods((store, radiologyCommonService = inject(RadiologyCommonService)) => ({
        getSexes: rxMethod<void>(
            pipe(
                switchMap(() => {
                    return radiologyCommonService.getSexes().pipe(
                        tap((response) => {
                            if (response.success && response.data) {
                                return store.addSexes(response.data);
                            }
                            return EMPTY;
                        }),
                    );
                }),
            ),
        ),

        getModalities: rxMethod<void>(
            pipe(
                switchMap(() => {
                    return radiologyCommonService.getModalities().pipe(
                        tap((response) => {
                            if (response.success && response.data) {
                                return store.addModalities(response.data);
                            }
                            return EMPTY;
                        }),
                    );
                }),
            ),
        ),
    })),
    withHooks({
        onInit(store, translateService = inject(TranslateService)) {
            store.getModalities();
            store.getSexes();
            const typologies = [
                {
                    code: EstensaModel.BrokerTypeFilter.TypeNotAsc,
                    examDescription: translateService.instant('ExamNotAsc'),
                    studyDescription: translateService.instant('StudyNotAsc'),
                },
                {
                    code: EstensaModel.BrokerTypeFilter.TypeAsc,
                    examDescription: translateService.instant('ExamAsc'),
                    studyDescription: translateService.instant('StudyAsc'),
                },
                {
                    code: EstensaModel.BrokerTypeFilter.TypeAscToPre,
                    examDescription: translateService.instant('ExamAscTo'),
                    studyDescription: translateService.instant('StudyAscTo'),
                },
                {
                    code: EstensaModel.BrokerTypeFilter.All,
                    examDescription: translateService.instant('AllExams'),
                    studyDescription: translateService.instant('AllStudies'),
                },
            ];
            store.addTypologies(typologies);
        },
    }),
);
